import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import OrderTracking from '../components/tracking/OrderTracking';
import { useAuth } from '../contexts/AuthContext';

const OrderTrackingPage = () => {
  const { orderNumber: urlOrderNumber } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [orderNumber, setOrderNumber] = useState(urlOrderNumber || '');
  const [searchedOrderNumber, setSearchedOrderNumber] = useState(urlOrderNumber || '');

  const handleSearch = (e) => {
    e.preventDefault();
    if (orderNumber.trim()) {
      setSearchedOrderNumber(orderNumber.trim());
      navigate(`/track/${orderNumber.trim()}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            מעקב הזמנה
          </h1>
          <p className="text-lg text-gray-600">
            עקוב אחרי ההזמנה שלך בזמן אמת
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <form onSubmit={handleSearch} className="space-y-4">
            <div>
              <label htmlFor="orderNumber" className="block text-sm font-medium text-gray-700 mb-2">
                הכנס מספר הזמנה
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  id="orderNumber"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="ORD-1234567890-123"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
                  required
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center font-semibold"
                >
                  <MagnifyingGlassIcon className="w-5 h-5 ml-2" />
                  חפש
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                מספר ההזמנה נמצא באימייל האישור או בדף ההזמנות שלך
              </p>
            </div>

            {isAuthenticated && (
              <div className="pt-4 border-t">
                <button
                  type="button"
                  onClick={() => navigate('/account/orders')}
                  className="text-primary-600 hover:text-primary-700 flex items-center font-medium"
                >
                  <ArrowRightIcon className="w-4 h-4 ml-1" />
                  צפה בכל ההזמנות שלי
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Tracking Results */}
        {searchedOrderNumber && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <OrderTracking orderNumber={searchedOrderNumber} showTitle={false} />
          </div>
        )}

        {/* Help Section */}
        {!searchedOrderNumber && (
          <div className="bg-blue-50 rounded-lg p-6 mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              איך למצוא את מספר ההזמנה?
            </h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="font-semibold ml-2">1.</span>
                <span>בדוק באימייל האישור שקיבלת לאחר ביצוע ההזמנה</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold ml-2">2.</span>
                <span>היכנס לחשבון שלך ובדוק בדף "ההזמנות שלי"</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold ml-2">3.</span>
                <span>מספר ההזמנה מתחיל ב-ORD ואחריו מספרים</span>
              </li>
            </ul>

            {!isAuthenticated && (
              <div className="mt-6 pt-6 border-t border-blue-200">
                <p className="text-gray-700 mb-3">
                  יש לך חשבון? התחבר כדי לראות את כל ההזמנות שלך
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="btn btn-primary"
                >
                  התחבר לחשבון
                </button>
              </div>
            )}
          </div>
        )}

        {/* Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MagnifyingGlassIcon className="w-8 h-8 text-primary-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">מעקב בזמן אמת</h3>
            <p className="text-sm text-gray-600">
              עדכונים מיידיים על מיקום החבילה שלך
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">התראות אוטומטיות</h3>
            <p className="text-sm text-gray-600">
              קבל התראות על כל שינוי בסטטוס ההזמנה
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">מאובטח ואמין</h3>
            <p className="text-sm text-gray-600">
              מידע מעודכן ומאומת על ההזמנה שלך
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingPage;

